import axiosInstance, { extractErrorMessage } from './axiosConfig';
import type { KnowledgeBaseCreate, KnowledgeBaseUpdate, KnowledgeBaseListApiResponse,
              KnowledgeBaseDetailApiResponse, KnowledgeBaseApiResponse, DocumentUploadApiResponse,
              DocumentApiResponse, StandardApiResponse } from '../types/knowledgeBase';
import { PaginatedResponse } from '../types/user';
import type { KnowledgeBaseResponse } from '../types/knowledgeBase';

// List all knowledge bases
export const listKnowledgeBases = async (
  signal?: AbortSignal,
  workspaceId?: number // Optional: omit to get KBs from ALL accessible workspaces (TD users)
): Promise<KnowledgeBaseListApiResponse> => {
  try {
    const params: Record<string, unknown> = {};

    // Only add workspace_id if provided (undefined = fetch from all accessible workspaces)
    if (workspaceId !== undefined) {
      params.workspace_id = workspaceId;
    }

    const response = await axiosInstance.get('/knowledge-bases', { params, signal });
    return response.data;
  } catch (err: unknown) {
    // Don't throw error if request was aborted (user triggered new search or React StrictMode)
    if (err instanceof Error && (err.name === 'AbortError' || err.name === 'CanceledError')) {
      throw err; // Re-throw original error to preserve error name
    }
    throw new Error(
      extractErrorMessage(
        err instanceof Error ? err : new Error(String(err)),
        'Falha ao buscar bases de conhecimento'
      )
    );
  }
};

// List knowledge bases with pagination (for infinite scroll)
export const listKnowledgeBasesWithPagination = async (
  page: number = 1,
  limit: number = 20,
  search?: string,
  signal?: AbortSignal,
  workspaceId?: number // Optional: omit to get KBs from ALL accessible workspaces (TD users)
): Promise<PaginatedResponse<KnowledgeBaseResponse>> => {
  try {
    const skip = (page - 1) * limit;
    const params: Record<string, unknown> = { skip, limit };

    // Only add workspace_id if provided (undefined = fetch from all accessible workspaces)
    if (workspaceId !== undefined) {
      params.workspace_id = workspaceId;
    }

    if (search) {
      params.search = search;
    }

    const response = await axiosInstance.get('/knowledge-bases', { params, signal });
    return response.data;
  } catch (err: unknown) {
    // Don't throw error if request was aborted (user triggered new search or React StrictMode)
    if (err instanceof Error && (err.name === 'AbortError' || err.name === 'CanceledError')) {
      throw err; // Re-throw original error to preserve error name
    }
    throw new Error(
      extractErrorMessage(
        err instanceof Error ? err : new Error(String(err)),
        'Falha ao buscar bases de conhecimento'
      )
    );
  }
};

// Get single knowledge base details
export const getKnowledgeBase = async (id: number): Promise<KnowledgeBaseDetailApiResponse> => {
  try {
    const response = await axiosInstance.get(`/knowledge-bases/${id}`);
    return response.data;
  } catch (err: unknown) {
    throw new Error(
      extractErrorMessage(
        err instanceof Error ? err : new Error(String(err)),
        'Falha ao buscar base de conhecimento'
      )
    );
  }
};

// Create new knowledge base
export const createKnowledgeBase = async (data: KnowledgeBaseCreate): Promise<KnowledgeBaseApiResponse> => {
  try {
    const response = await axiosInstance.post('/knowledge-bases', data);
    return response.data;
  } catch (err: unknown) {
    throw new Error(
      extractErrorMessage(
        err instanceof Error ? err : new Error(String(err)),
        'Falha ao criar base de conhecimento'
      )
    );
  }
};

// Update knowledge base
export const updateKnowledgeBase = async (id: number, data: KnowledgeBaseUpdate): Promise<KnowledgeBaseApiResponse> => {
  try {
    const response = await axiosInstance.put(`/knowledge-bases/${id}`, data);
    return response.data;
  } catch (err: unknown) {
    throw new Error(
      extractErrorMessage(
        err instanceof Error ? err : new Error(String(err)),
        'Falha ao atualizar base de conhecimento'
      )
    );
  }
};

// Delete knowledge base
export const deleteKnowledgeBase = async (id: number): Promise<StandardApiResponse> => {
  try {
    const response = await axiosInstance.delete(`/knowledge-bases/${id}`);
    return response.data;
  } catch (err: unknown) {
    throw new Error(
      extractErrorMessage(
        err instanceof Error ? err : new Error(String(err)),
        'Falha ao excluir base de conhecimento'
      )
    );
  }
};

// Upload document to knowledge base
export const uploadDocument = async (knowledgeBaseId: number, file: File): Promise<DocumentUploadApiResponse> => {
  try {
    const formData = new FormData();
    formData.append('file', file);

    const response = await axiosInstance.post(
      `/knowledge-bases/${knowledgeBaseId}/documents`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      }
    );
    return response.data;
  } catch (err: unknown) {
    throw new Error(
      extractErrorMessage(
        err instanceof Error ? err : new Error(String(err)),
        'Falha ao fazer upload do documento'
      )
    );
  }
};

// Delete document from knowledge base
export const deleteDocument = async (knowledgeBaseId: number, documentId: number): Promise<StandardApiResponse> => {
  try {
    const response = await axiosInstance.delete(
      `/knowledge-bases/${knowledgeBaseId}/documents/${documentId}`
    );
    return response.data;
  } catch (err: unknown) {
    throw new Error(
      extractErrorMessage(
        err instanceof Error ? err : new Error(String(err)),
        'Falha ao excluir documento'
      )
    );
  }
};

// Get document content
export const getDocument = async (knowledgeBaseId: number, documentId: number): Promise<DocumentApiResponse> => {
  try {
    const response = await axiosInstance.get(
      `/knowledge-bases/${knowledgeBaseId}/documents/${documentId}`
    );
    return response.data;
  } catch (err: unknown) {
    throw new Error(
      extractErrorMessage(
        err instanceof Error ? err : new Error(String(err)),
        'Falha ao buscar documento'
      )
    );
  }
};